import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import InventoryPage, { columns as ALL_COLUMNS, ColDef, getCurrentMonth } from './InventoryPage';
import { useAuth } from '../contexts/AuthContext';

/**
 * "My Inventory" — a reflection of the Inventory section with the same filter
 * dropdowns, but limited to the columns below and view-only except Status,
 * Discount and At-Risk Expected Payment. For most users those three are editable
 * for the CURRENT calendar month only — switching the month filter to any other
 * month (e.g. May) makes the whole table read-only.
 *
 * [ALL-PM-SENIOR 2026-06-23] [MYINV-PM-CURMONTH 2026-06-24] [MYINV-FUTURE 2026-06-25]
 * The all-PM seniors (Sana/Amaan, server flag `inventory_all_pms`) get a wider grant:
 * they edit Status, Discount, At-Risk Expected Payment AND the PM column (the same
 * roster dropdown as the Inventory tab) on the CURRENT month and ANY FUTURE month
 * (past months stay read-only), so they can manage this month's live inventory and
 * all upcoming inventory — including reassigning rows whose PM has left HubSpot.
 * Edits write the shared inventory.pm column, so the Inventory tab reflects them
 * (admins keep full edit).
 *
 * The PM dropdown is scoped to the signed-in user:
 *   - Admins              → every PM (with an "All PMs" option), defaulting to "All PMs".
 *   - All-PM seniors      → same as admins for the dropdown (every PM + "All PMs",
 *     (Sana/Amaan)           defaulting to it) — write rules below still apply.
 *   - Other seniors       → every PM in the list, but default to their own name (no
 *                           "All PMs" option). [see inventoryScope.isAllPmInventorySenior]
 *   - Team leads/owners   → themselves (default) + their team members in the dropdown.
 *   - Everyone else       → just themselves (the dropdown is effectively locked).
 * The allowed names + the data itself are enforced server-side; here we only set
 * the default selection and whether the "All PMs" option is shown.
 */

// Columns to show, in display order (matches the agreed layout). Everything
// here is read-only except the two listed in EDITABLE_KEYS.
const VISIBLE_KEYS = [
  // [CAMPAIGN-STATUS 2026-07-06] New/Existing-campaign column, before Type (as on Inventory).
  // [AM-NAMES 2026-09-04, user] AM Names sits immediately after PM Name, same as on the
  // Inventory tab — the column def itself is shared, so the two tabs cannot drift.
  'company_record_id', 'website', 'email', 'campaign_status', 'type', 'pm', 'am_names', 'brand',
  'amount', 'billing_date', 'month', 'status',
  'total_payment_received', 'refunded_disputed', 'recurring_retention',
  'recurring_so', 'one_time_so', 'sales_pending_payment', 'recovery',
  'discount', 'at_risk_expected_payment', 'unmapped_payments',
];

// Only these stay editable; all other columns become view-only. (And even these
// edit only in the current month — see currentMonthEditOnly below.)
// [AM-ALL-PMS 2026-09-10, user] AM Names is editable for EVERYONE who sees My Inventory now
// (their own rows, current + future months, one cell at a time) — no longer admins/seniors only.
const EDITABLE_KEYS = new Set(['status', 'discount', 'at_risk_expected_payment', 'am_names']);

// [MYINV-RECURRING-ONLY 2026-09-24, user] My Inventory shows RECURRING rows only: One-Time and
// PIA rows are hidden here (every month, search included). The Inventory tab shows every type.
const HIDDEN_TYPES = ['One-Time', 'PIA'];

// Reuse the canonical column defs (labels, widths, options, types) and just flip
// editability per the given key set — keeps My Inventory in sync with Inventory
// automatically. Built per-user so the all-PM seniors can also get `pm` editable.
function buildMyColumns(editableKeys: Set<string>): ColDef[] {
  return VISIBLE_KEYS
    .map((key) => ALL_COLUMNS.find((c) => c.key === key))
    .filter((c): c is ColDef => !!c)
    .map((c) => ({ ...c, editable: editableKeys.has(c.key) }));
}

const MyInventoryPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const isSenior = !!user?.is_senior;
  // [MYINV-PM-ACCESS 2026-06-26] PM-column edit access is ROLE-based: admins and seniors
  // may (re)assign the PM in My Inventory; everyone else is limited to Status / Discount /
  // At-Risk Expected Payment.
  // [AM-NAMES 2026-09-04, user] AM Names used to ride with the PM column throughout.
  // [AM-ALL-PMS 2026-09-10, user] Not any more: EVERY user edits AM Names on their own rows,
  // current + future months, one cell at a time. Bulk AM stays admins/seniors only. The server
  // enforces the same (NON_ADMIN_EDITABLE in the PUT; bulkDenyReason unchanged).
  const canEditPmCol = isAdmin || isSenior;

  // [MYINV-DEEPLINK 2026-06-26] Honor a ?month=YYYY-MM in the URL (used by the Google Chat
  // "Assign PMs" alert's "Review inventory" button, which deep-links to the affected — e.g.
  // next — month). Validated to YYYY-MM; anything else falls back to the current month.
  const [searchParams] = useSearchParams();
  const urlMonth = searchParams.get('month');
  const initialMonth = urlMonth && /^\d{4}-\d{2}$/.test(urlMonth) ? urlMonth : getCurrentMonth();

  // [MYINV-SEE-ALL 2026-06-26] Admins + seniors land on "All PMs" (the All PMs option
  // visible) so they see EVERY inventory row by default — the server `seeAll` scope returns
  // all rows, while the PM dropdown still lists only ACTIVE HubSpot PMs. Everyone else lands
  // on their own name, scoped to their own rows.
  const allPmsDefault = isAdmin || isSenior;

  // Admins + seniors additionally get the PM column editable (assignment, via the same
  // roster dropdown as the Inventory tab). For them all four editable columns
  // (Status/Discount/At-Risk/PM) edit on the current + any future month (see
  // currentAndFutureMonthEditKeys below); everyone else gets only Status/Discount/At-Risk,
  // current-month-only.
  const columns = useMemo(
    () => buildMyColumns(canEditPmCol ? new Set([...EDITABLE_KEYS, 'pm']) : EDITABLE_KEYS),
    [canEditPmCol],
  );

  return (
    <InventoryPage
      title="My Inventory"
      // All-PM users default to "All PMs" (empty filter) and keep the "All PMs"
      // option; everyone else lands on their own name with that option hidden.
      defaultPm={allPmsDefault ? '' : (user?.pm_name || '')}
      hideAllPms={!allPmsDefault}
      defaultMonth={initialMonth}
      columns={columns}
      // [MYINV-ROW-TINT 2026-06-25] Tint current-month rows by payment status (red =
      // fully received but still has an Unmapped Payments balance, amber = partial
      // payment). My Inventory only — the main Inventory tab is unaffected.
      rowStatusTint
      // [AM-REMINDER 2026-09-24, user] "Add the AM on your campaigns" banner above the
      // Performance Snapshot — My Inventory ONLY. It counts the rows in view this user can set
      // an AM on (current + future months) and hides itself once they all have one.
      amReminder
      showActions={false}
      // [MYINV-DOWNLOAD 2026-07-15] "Download record" — the SAME export as the
      // Inventory tab (same modal, same CSV, same endpoint), shown here for ADMINS
      // + SENIORS only; nobody else sees the button. Needs its own prop because
      // showActions={false} above (no Add / no row actions) would otherwise take
      // the download with it. The server still enforces PM scope on the export, so
      // this only ever widens the UI, never the data a viewer can reach.
      showDownloadButton={isAdmin || isSenior}
      // [MYINV-RECURRING-ONLY 2026-09-24, user] Recurring rows only: One-Time and PIA are hidden.
      // The row count, the toolbar "Placed Inventory", and the "Add the AM" banner all follow the
      // visible rows, so the toolbar figure now equals the Performance Snapshot's Recurring-only
      // Placed Inventory tile, and is BELOW the Inventory tab's all-types total by the One-Time +
      // PIA amount. (Reverses [MYINV-ONE-TIME 2026-09-03], when One-Time rows were shown here to
      // match the Inventory tab; the user now wants this tab Recurring-only.) The Download
      // modal keeps its own Type picker, so admins/seniors can still export every type.
      excludeTypes={HIDDEN_TYPES}
      // [MYINV-EXPORT-RECURRING 2026-09-29, user] …but it now STARTS at Recurring, so the file
      // matches this tab (it used to default to all types: 9 more rows than the tab showed).
      exportDefaultTypes={['Recurring']}
      showTypeFilter={false}
      showBrandFilter={false}
      showKpis
      // [CAMPAIGN-HISTORY 2026-09-04, user] Clicking the website here opens that campaign's whole
      // history (/campaign-history) in the same tab. My Inventory ONLY — on the Inventory tab the
      // same click opens the website editor, so a link there would fight it; the user will add a
      // separate button on that tab later.
      websiteOpensHistory
      logSection="My Inventory"
      // Non-admins are clamped to the current month by default; any other month
      // (incl. via the filter) is read-only. Admins are exempt (edit any month —
      // see InventoryPage isCellEditable); seniors extend to future months below.
      currentMonthEditOnly
      // [MYINV-FUTURE 2026-06-25] [MYINV-PM-ACCESS 2026-06-26] [MYINV-ADMIN-ALLMONTHS 2026-07-20]
      // SENIORS edit Status, Discount, At-Risk Expected Payment AND PM on the current month
      // and ANY future month (past months read-only). ADMINS edit every month incl. past
      // (InventoryPage exempts admins from currentMonthEditOnly). Everyone else keeps the
      // current-month-only rule for Status / Discount / At-Risk (and no PM).
      // [AM-ALL-PMS 2026-09-10, user] Everyone else now gets AM Names on current + future months
      // too (the server's previous-month lock already allowed future months for own rows).
      currentAndFutureMonthEditKeys={canEditPmCol ? ['status', 'discount', 'at_risk_expected_payment', 'pm', 'am_names'] : ['am_names']}
      // [BULK-EDIT 2026-08-03] Multi-row edit here is for PM REASSIGNMENT ONLY (user request):
      // the bulk column picker offers PM Name and nothing else, even though Status / Discount /
      // At-Risk stay editable one cell at a time. Consequence by design: a user who cannot edit
      // the PM column (i.e. not an admin/senior) has nothing to bulk-edit, so the checkbox
      // column and selection bar don't render for them at all.
      // [AM-NAMES 2026-09-04, user] AM Names joins PM Name as a bulk-assignable column, so the
      // picker now offers two. The bulk value stays a SINGLE name (the user's ruling) even
      // though the cell editor is multi-select — so a bulk write REPLACES the row's AMs.
      // [AM-ALL-PMS 2026-09-10, user] Bulk stays admins/seniors only — everyone else edits AM
      // one cell at a time. ⛔ Do NOT pass [] for them: an empty list means "no pin", which would
      // open bulk on Status/Discount/At-Risk. ['pm'] is not editable for them, so bulk vanishes.
      bulkEditKeys={canEditPmCol ? ['pm', 'am_names'] : ['pm']}
      // [MYINV-PM-NO-BLANK 2026-09-28, user] Once a row has a PM Name nobody can make it blank here
      // (no blank option in the cell once set; bulk edit can only assign a name). My Inventory ONLY.
      pmNoBlankOnceSet
      // [COL-RESIZE-ALL 2026-09-29, user] Its own remembered column widths, separate from Inventory's.
      columnResizeKey="my-inventory"
    />
  );
};

export default MyInventoryPage;
